import { useState } from "react";
import { configToUse } from "@/Keycloak";
import keycloak from "@/Keycloak";

// Define an interface for the streaming response data
interface InsightStreamData {
  text: string;
  status: string;
}

export function useInsights() {
  const [insightsLoading, setInsightsLoading] = useState<boolean>(false);
  const [insights, setInsights] = useState<string[]>([]);

  // Helper function to process the insight text into an array of bullet points
  const processInsightText = (text: string): string[] => {
    // Split by numbered points (1., 2., etc.) or bullet points
    const mainPoints = text.split(/\n\d+\./).filter(Boolean);

    if (mainPoints.length <= 1) {
      // If we don't have clear numbered points yet, return the whole text
      return text.trim() ? [text.trim()] : [];
    }

    const processedInsights: string[] = [];

    for (let point of mainPoints) {
      point = point.trim();
      if (!point) continue;

      // Add the main point
      if (!point.startsWith("Recommendations:")) {
        processedInsights.push(point);
      } else {
        // Handle recommendations section separately
        const recommendationText = point.replace("Recommendations:", "").trim();
        const recommendations = recommendationText.split(/\n\d+\./).filter(Boolean);

        for (const rec of recommendations) {
          if (rec.trim()) {
            processedInsights.push(`Recommendation: ${rec.trim()}`);
          }
        }
      }
    }

    return processedInsights;
  };

  const generateInsights = async (
    accountId: string,
    reportId: string,
    sectionId: string,
    promptOrId: { prompt_id?: string; prompt?: string },
  ) => {
    if (!accountId || !reportId || !sectionId) return;

    try {
      setInsightsLoading(true);

      // Initialize empty insights array
      setInsights([]);

      // Build request body - only include prompt_id OR prompt, not both
      const requestBody: { prompt_id?: string; prompt?: string } = {};
      if (promptOrId.prompt_id) {
        requestBody.prompt_id = promptOrId.prompt_id;
      } else if (promptOrId.prompt) {
        requestBody.prompt = promptOrId.prompt;
      }

      const response = await fetch(
        `${configToUse.VITE_APP_FERROUS_API_BASE_URL}/creative/${accountId}/report/${reportId}/sections/${sectionId}/insights/streaming/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${keycloak.token}`,
          },
          body: JSON.stringify(requestBody),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error("Response body is null");

      const decoder = new TextDecoder();
      let insightText = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n").filter((line) => line.trim() !== "");

        for (const line of lines) {
          if (line.startsWith("data:")) {
            try {
              const jsonStr = line.slice(5).trim();
              if (jsonStr === "[DONE]") break;

              const data = JSON.parse(jsonStr) as InsightStreamData;
              if (data.status === "in_progress" && data.text) {
                insightText += data.text;

                // Process the accumulated text into bullet points
                const processedInsights = processInsightText(insightText);

                setInsights(processedInsights);
              }
            } catch (e) {
              console.error("Error parsing streaming data:", e);
            }
          }
        }
      }
    } catch (error) {
      console.error("Error generating insights:", error);
      // Clear insights on error
      setInsights([]);
    } finally {
      setInsightsLoading(false);
    }
  };

  const clearInsights = () => {
    setInsights([]);
  };

  return {
    insightsLoading,
    insights,
    setInsights,
    generateInsights,
    clearInsights,
  };
}
